import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, Plus } from "lucide-react";
import { adminApi, apiError } from "../adminApi";
import { Panel, Pill, inputClass, buttonClass } from "../components/Bits";
import { HarvestShell, useHarvestHeader } from "./HarvestShell";
import { formatStamp } from "../../lib/harvestDates";

const COLUMNS = [
  ["queued", "Queued"],
  ["awaiting_approval", "Awaiting approval"],
  ["approved", "Approved"],
  ["packed", "Packed"],
  ["shipped", "Shipped / handed over"],
  ["delivered", "Delivered"],
  ["followup_due", "Follow-up due"],
];
const NEXT = {
  queued: "awaiting_approval",
  awaiting_approval: "approved",
  approved: "packed",
  packed: "shipped",
  shipped: "delivered",
  delivered: "followup_due",
};
const CARRIERS = {
  USPS: "https://tools.usps.com/go/TrackConfirmAction?tLabels=",
  UPS: "https://www.ups.com/track?tracknum=",
  FedEx: "https://www.fedex.com/fedextrack/?trknbr=",
};

export default function AdminHarvestFulfillment() {
  const { settings, pending } = useHarvestHeader();
  const [shipments, setShipments] = useState([]);
  const [packages, setPackages] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [draft, setDraft] = useState({ account_id: "", package_key: "", wave: "fall", method: "mail" });

  const load = () => adminApi.get("/crm/harvest/shipments")
    .then(({ data }) => setShipments(data.shipments))
    .catch((error) => toast.error(apiError(error)));

  useEffect(() => {
    load();
    adminApi.get("/crm/harvest/cadences").then(({ data }) => setPackages(data.packages)).catch(() => {});
    adminApi.get("/crm/harvest/accounts").then(({ data }) => setAccounts(data.accounts)).catch(() => {});
  }, []);

  const cap = settings?.gift_cap ?? 25;
  const warn = settings?.gift_warn ?? 15;

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(COLUMNS.map(([key]) => [key, []]));
    shipments.forEach((s) => { (map[s.status] || map.queued).push(s); });
    return map;
  }, [shipments]);

  const create = async () => {
    if (!draft.account_id || !draft.package_key) {
      toast.error("Pick an account and a package");
      return;
    }
    try {
      await adminApi.post("/crm/harvest/shipments", draft);
      toast.success("Shipment queued");
      setDraft({ ...draft, account_id: "" });
      load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail?.message || apiError(error));
    }
  };

  const move = async (shipment) => {
    const next = NEXT[shipment.status];
    if (!next) return;
    try {
      await adminApi.patch(`/crm/harvest/shipments/${shipment.id}`, { status: next });
      toast.success(`Moved to ${next.replace(/_/g, " ")}`);
      load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      if (detail?.error === "gift_cap_exceeded") {
        toast.error(detail.message, { duration: 8000 });
      } else {
        toast.error(apiError(error));
      }
    }
  };

  const patch = async (id, body) => {
    try {
      await adminApi.patch(`/crm/harvest/shipments/${id}`, body);
      load();
    } catch (error) { toast.error(apiError(error)); }
  };

  const downloadGiftLog = async () => {
    try {
      const { data } = await adminApi.get("/crm/harvest/gift-log.csv", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(data);
      link.download = "harvest-gift-log.csv";
      link.click();
    } catch (error) { toast.error(apiError(error)); }
  };

  const capTone = (total) => (total > cap ? "text-terra" : total > warn ? "text-gold" : "text-cream/60");

  return (
    <HarvestShell settings={settings} pending={pending}>
      <Panel
        title="Queue a shipment"
        testid="harvest-shipment-form"
        action={
          <button type="button" onClick={downloadGiftLog} data-testid="harvest-gift-log-export"
            className="inline-flex items-center gap-2 rounded-full border border-cream/15 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-cream/60 hover:text-cream">
            <Download className="h-3.5 w-3.5" /> Gift log CSV
          </button>
        }
      >
        <div className="grid gap-3 sm:grid-cols-5">
          <select value={draft.account_id} onChange={(e) => setDraft({ ...draft, account_id: e.target.value })}
            data-testid="harvest-shipment-account" className={inputClass}>
            <option value="">Select account…</option>
            {accounts.map((a) => (
              <option key={a.account_id} value={a.account_id}>{a.company}</option>
            ))}
          </select>
          <select value={draft.package_key} onChange={(e) => setDraft({ ...draft, package_key: e.target.value })}
            data-testid="harvest-shipment-package" className={inputClass}>
            <option value="">Select package…</option>
            {packages.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <select value={draft.wave} onChange={(e) => setDraft({ ...draft, wave: e.target.value })}
            data-testid="harvest-shipment-wave" className={inputClass}>
            {["fall", "winter", "t2b1", "t2b2", "t3"].map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
          <select value={draft.method} onChange={(e) => setDraft({ ...draft, method: e.target.value })}
            data-testid="harvest-shipment-method" className={inputClass}>
            {["mail", "hand", "thanksio"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <button type="button" onClick={create} className={buttonClass} data-testid="harvest-shipment-create">
            <Plus className="h-3.5 w-3.5" /> Queue
          </button>
        </div>
        <p className="mt-3 text-xs text-cream/40">
          Gift cap ${cap} per recipient for the season, amber over ${warn}. Nothing is ordered from
          here — queue it, approve it, then place the order in Thanks.io.
        </p>
      </Panel>

      <div className="grid gap-4 xl:grid-cols-4" data-testid="harvest-kanban">
        {COLUMNS.map(([key, label]) => (
          <div key={key} className="rounded-xl border border-cream/10 bg-[#10211A] p-4"
            data-testid={`harvest-column-${key}`}>
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold/80">
              {label} · {byColumn[key].length}
            </p>
            <div className="mt-4 space-y-3">
              {byColumn[key].map((shipment) => (
                <div key={shipment.id} data-testid={`harvest-card-${shipment.id}`}
                  className="rounded-lg border border-cream/10 bg-cream/[0.03] p-3">
                  <p className="text-sm text-cream">{shipment.company}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/40">
                    {shipment.package_key} · {shipment.wave} · {shipment.method}
                  </p>
                  <p className={`mt-2 font-mono text-xs ${capTone(shipment.season_gift_total || 0)}`}
                    data-testid={`harvest-card-gift-${shipment.id}`}>
                    Gift ${shipment.gift_value?.toFixed(2)} · season ${(shipment.season_gift_total || 0).toFixed(2)}
                  </p>
                  {shipment.education_only ? <Pill tone="sage">Education only</Pill> : null}
                  {!shipment.window_ok ? (
                    <p className="mt-2 flex items-start gap-1.5 rounded border border-terra/40 bg-terra/10 px-2 py-1.5 text-[11px] text-terra"
                      data-testid={`harvest-card-window-${shipment.id}`}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {shipment.window_note}
                    </p>
                  ) : null}
                  {["shipped", "delivered", "followup_due"].includes(shipment.status) ? (
                    <div className="mt-2 space-y-1.5">
                      <input
                        defaultValue={shipment.tracking_no || ""}
                        placeholder="Tracking number"
                        onBlur={(e) => e.target.value !== (shipment.tracking_no || "")
                          && patch(shipment.id, { tracking_no: e.target.value })}
                        data-testid={`harvest-card-tracking-${shipment.id}`}
                        className={`${inputClass} py-1.5 text-xs`}
                      />
                      <select
                        defaultValue={shipment.carrier || ""}
                        onChange={(e) => patch(shipment.id, { carrier: e.target.value })}
                        data-testid={`harvest-card-carrier-${shipment.id}`}
                        className={`${inputClass} py-1.5 text-xs`}
                      >
                        <option value="">Carrier…</option>
                        {Object.keys(CARRIERS).map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {shipment.tracking_no && shipment.carrier ? (
                        <a href={`${CARRIERS[shipment.carrier]}${shipment.tracking_no}`}
                          target="_blank" rel="noreferrer"
                          className="block text-[11px] text-gold hover:underline">
                          Track this parcel
                        </a>
                      ) : null}
                    </div>
                  ) : null}
                  {shipment.followup_due ? (
                    <p className="mt-2 text-[11px] text-sage">
                      Follow-up due {shipment.followup_due}
                    </p>
                  ) : null}
                  <p className="mt-2 text-[10px] text-cream/30">{formatStamp(shipment.created_at)}</p>
                  {NEXT[shipment.status] ? (
                    <button type="button" onClick={() => move(shipment)}
                      data-testid={`harvest-card-advance-${shipment.id}`}
                      className="mt-3 w-full rounded-full border border-cream/15 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-cream/60 transition-colors duration-200 hover:border-gold/50 hover:text-gold">
                      Move to {NEXT[shipment.status].replace(/_/g, " ")}
                    </button>
                  ) : null}
                </div>
              ))}
              {!byColumn[key].length ? <p className="text-xs text-cream/30">Empty</p> : null}
            </div>
          </div>
        ))}
      </div>

      <Panel title="Package catalog" testid="harvest-package-catalog">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-cream/35">
                {["Package", "Tiers", "Gift value", "Mailed cost", "Hand cost", "Ship windows", "Notes"]
                  .map((h) => <th key={h} className="pb-3 pr-4 font-normal">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {packages.map((p) => (
                <tr key={p.key} className="border-t border-cream/5" data-testid={`harvest-package-${p.key}`}>
                  <td className="py-3 pr-4 text-cream">{p.name}</td>
                  <td className="py-3 pr-4 text-cream/60">{(p.tiers || []).join(", ")}</td>
                  <td className={`py-3 pr-4 font-mono text-xs ${capTone(p.gift_value)}`}>
                    ${p.gift_value?.toFixed(2)}
                  </td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/60">${p.cost_mailed?.toFixed(2)}</td>
                  <td className="py-3 pr-4 font-mono text-xs text-cream/60">
                    {p.cost_hand ? `$${p.cost_hand.toFixed(2)}` : "—"}
                  </td>
                  <td className="py-3 pr-4 text-xs text-cream/50">
                    {(p.ship_windows || []).map((w) => `${w.wave}: ${w.start} – ${w.end}`).join(" · ")}
                  </td>
                  <td className="py-3 pr-4 text-xs text-cream/50">{p.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </HarvestShell>
  );
}
